// Contexto de permisos por modulo. GET /users/me/permisos ya existe en el
// backend (routers/usuarios_admin.py) y devuelve el objeto real por modulo
// para el usuario en sesion (Administrador recibe acceso total). El shell
// (VerticalSidebar) usa perm() para filtrar el sidebar - ver ese archivo.
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { PATH } from "../redux/utils";
import { MODULO_KEYS, ACTION_KEYS } from "../constants/modulos";
import { PERMISO_SOLICITAR_CAMBIOS } from "../utils/permisosEspeciales";

const PermisosContext = createContext(null);

const buildDefault = () =>
  Object.fromEntries(MODULO_KEYS.map((m) => [m, Object.fromEntries(ACTION_KEYS.map((a) => [a, false]))]));

export function PermisosProvider({ children }) {
  const [permisos, setPermisos] = useState(buildDefault());
  const [loaded, setLoaded] = useState(false);

  const fetchPermisos = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setPermisos(buildDefault());
      setLoaded(true);
      return;
    }
    try {
      const { data } = await axios.get(`${PATH}/users/me/permisos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const next = data || buildDefault();
      // Solo actualiza si cambio algo, para no re-renderizar en cada sondeo.
      setPermisos((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    } catch (err) {
      // Un fallo de red en el sondeo no debe borrar los permisos vigentes;
      // solo se vacian si el servidor rechaza la sesion.
      const status = err?.response?.status;
      if (status === 401 || status === 403) setPermisos(buildDefault());
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchPermisos();
  }, [fetchPermisos]);

  useEffect(() => {
    const handler = () => fetchPermisos();
    window.addEventListener("permisos-refresh", handler);
    // Tiempo real: el admin puede cambiar permisos desde otra sesion, asi que
    // se sondea cada pocos segundos y al volver a la pestana.
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchPermisos();
    }, 4000);
    const onVisible = () => { if (document.visibilityState === "visible") fetchPermisos(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", handler);
    return () => {
      window.removeEventListener("permisos-refresh", handler);
      window.removeEventListener("focus", handler);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [fetchPermisos]);

  // Sin acceso al modulo (modulo=false) no hay ninguna accion permitida.
  const perm = (module, action) => {
    const p = permisos?.[module];
    if (!p) return false;
    if (action === "modulo") return Boolean(p.modulo);
    return Boolean(p.modulo && p[action]);
  };

  return (
    <PermisosContext.Provider value={{ permisos, perm, loaded, refresh: fetchPermisos }}>
      {children}
    </PermisosContext.Provider>
  );
}

export const usePermisos = () => useContext(PermisosContext);

// true si el usuario en sesion puede enviar "Solicitar cambio". Administrador
// siempre puede; para el resto se lee `puede_solicitar_cambios` de
// GET /users/me/permisos (se sondea cada pocos segundos, ver arriba).
export const usePuedeSolicitarCambios = () => {
  const ctx = usePermisos();
  if (String(localStorage.getItem("role") || "").toLowerCase() === "administrador") return true;
  return ctx?.permisos?.[PERMISO_SOLICITAR_CAMBIOS] === true;
};
